import React from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Globe, LogOut, Plus, Settings, BookOpen } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Logomark } from "@/components/marketing/Navbar";

const items = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/domains", label: "Domains", icon: Globe },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#FBF9F4]">
      {/* sidebar */}
      <aside className="w-64 border-r border-stone-200 hidden md:flex flex-col bg-white" data-testid="dashboard-sidebar">
        <Link to="/" className="flex items-center gap-2.5 px-5 h-16 border-b border-stone-200">
          <Logomark />
          <div className="font-display font-bold tracking-tight ink">TrustDmarc</div>
        </Link>
        <div className="p-4">
          <button
            onClick={() => navigate("/dashboard/domains/new")}
            className="w-full btn-accent justify-center"
            data-testid="sidebar-add-domain"
          >
            <Plus className="h-4 w-4" /> Add domain
          </button>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`sidebar-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-orange-50 text-orange-700" : "text-stone-700 hover:text-stone-900 hover:bg-stone-50"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
          <div className="pt-4 mt-4 border-t border-stone-200">
            <Link to="/docs" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-stone-700 hover:text-stone-900 hover:bg-stone-50">
              <BookOpen className="h-4 w-4" strokeWidth={2} />
              Docs
            </Link>
          </div>
        </nav>
        <div className="p-3 border-t border-stone-200">
          {user && (
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="h-9 w-9 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden">
                {user.picture
                  ? <img src={user.picture} alt="" className="h-full w-full object-cover" />
                  : <span className="text-sm font-bold text-orange-700">{user.name?.[0] ?? "U"}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium ink truncate">{user.name}</div>
                <div className="text-xs ink-muted truncate">{user.email}</div>
              </div>
              <button onClick={logout} className="p-2 text-stone-400 hover:text-stone-900 rounded-md hover:bg-stone-100" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* main */}
      <main className="flex-1 min-w-0">
        <div className="h-16 border-b border-stone-200 bg-white flex items-center justify-between px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <span className="pulse-dot"></span>
            <span className="text-sm font-medium ink">Live · org_4a2c</span>
            <span className="text-xs ink-muted font-mono">lhr1</span>
          </div>
          <div className="flex items-center gap-3 text-xs ink-muted font-mono">v1.0</div>
        </div>
        <div className="p-8 lg:p-10 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
